/// <amd-module name="Build/DesignerExtensions/YamlSnippetForm/SnippetForm" />
import * as Resources from "Build/DesignerExtensions/YamlSnippetForm/Resources";
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { Accordion } from "VSSUI/Accordion";
import { Button } from "VSSUI/Button";
import { FocusZone } from "VSSUI/FocusZone";
import { Link } from "VSSUI/Link";
import { css } from "VSSUI/Util";

import { TaskGroupDefinition } from "DistributedTask/Client/Contracts/TaskAgent";
import { YamlSnippet, YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";

import { UnparentedInputsGroupName } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { SnippetFormProvider } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Types";
import { getSnippetInputGroups, SnippetInputList } from "Build/DesignerExtensions/YamlSnippetForm/Components/SnippetInputGroup";
import { SnippetFormSource } from "Build/DesignerExtensions/YamlSnippetForm/Sources/SnippetFormSource";
import { SnippetFormStore } from "Build/DesignerExtensions/YamlSnippetForm/Stores/SnippetFormStore";
import { exportYaml } from "Build/DesignerExtensions/YamlSnippetForm/Utilities/YamlExport";

import { TelemetryConstants } from "Build/DesignerExtensions/YamlAssistantLibrary/Contracts/Constants";
import { EnhancedLabel } from "./Components/EnhancedLabel";

export interface SnippetFormState {
    snippetInputsByGroup: { [key: string]: YamlSnippetInput[] };
    visibleGroups: TaskGroupDefinition[];
    isSnippetSupported: boolean;
    error?: Error;
}

export class SnippetForm extends VssComponent<SnippetFormProvider, SnippetFormState> {
    private _store: SnippetFormStore;
    private _source: SnippetFormSource;

    constructor(props: SnippetFormProvider) {
        super(props);

        this.state = {
            snippetInputsByGroup: {},
            visibleGroups: [],
            isSnippetSupported: true
        };
    }

    public render(): JSX.Element {
        return <div className={"yaml-snippet-form flex-column flex-grow"} style={this.props.customFormStyles}>{this._content()}</div>;
    }

    private _content(): JSX.Element | null {
        if (this.state.error && this.state.error.message) {
            return (
                <div>
                    <Button
                        className={"yaml-snippet-back-button"}
                        onClick={this.props.onDismiss}
                        subtle={true}
                        iconProps={{ iconName: "Back" }}
                        ariaLabel={Resources.CancelSnippetYaml}
                    />
                    <span>{format(Resources.FailedToLoadSnippet, this.state.error.message)}</span>
                </div>
            );
        }

        const ungroupedInputs = this.state.snippetInputsByGroup[UnparentedInputsGroupName];
        let infoMessage = "";
        if (this.props.snippet.helpMarkdown) {
            infoMessage = this.props.snippet.description.concat("\n\n", this.props.snippet.helpMarkdown);
        } else {
            infoMessage = this.props.snippet.description;
        }

        return (
            <>
                <FocusZone focusOnMount={true}>
                    <div className={css(this.props.className || "yaml-snippet-form-content", "flex-grow", "v-scroll-auto", "h-scroll-hidden")}>
                        <div className={css(this.props.innerContentClassName || "yaml-snippet-form-inner-content")}>
                            {!this.props.hideHeader && (
                                <span className={"flex-row snippet-form-header"}>
                                    <Button
                                        className={"yaml-snippet-back-button"}
                                        onClick={this.props.onDismiss}
                                        subtle={true}
                                        iconProps={{ iconName: "Back" }}
                                        ariaLabel={Resources.CancelSnippetYaml}
                                    />
                                    <EnhancedLabel
                                        className={"snippet-name-header title-s"}
                                        title={this.props.snippet.friendlyName}
                                        titleClassName={"text-ellipsis"}
                                        documentation={infoMessage}
                                    />
                                </span>
                            )}
                            {ungroupedInputs && ungroupedInputs.length > 0 && (
                                <SnippetInputList
                                    className={"ungrouped"}
                                    inputs={ungroupedInputs}
                                    inputValueProvider={this._store}
                                    serviceConnectionProvider={this._source}
                                    onError={this._onError}
                                />
                            )}
                            {this.state.visibleGroups.length > 0 && (
                                <Accordion
                                    sections={getSnippetInputGroups(
                                        this.state.snippetInputsByGroup,
                                        this.state.visibleGroups,
                                        this._store.isGroupCollapsed,
                                        this._store,
                                        this._source,
                                        this._onError
                                    )}
                                    onSectionClicked={this._store.toggleGroup}
                                />
                            )}
                        </div>
                    </div>
                </FocusZone>
                {!this.props.hideFooter && (
                    <div className={"yaml-snippet-form-footer"}>
                        {this.props.snippet.documentationUrl && (
                            <Link
                                className={"snippet-documentation"}
                                href={this.props.snippet.documentationUrl}
                                target={"_blank"}
                                rel={"noopener noreferrer"}
                            >
                                {Resources.TaskReference}
                            </Link>
                        )}
                        <Button primary={true} onClick={this._addSnippetToYaml}>
                            {Resources.AddSnippetYaml}
                        </Button>
                    </div>
                )}
            </>
        );
    }

    public componentDidMount() {
        super.componentDidMount();
        this._load();
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        this._store && this._store.dispose();
    }

    public componentDidUpdate(prevProps: SnippetFormProvider, prevState: SnippetFormState) {
        if (prevProps.snippet !== this.props.snippet || prevProps.startingParameters !== this.props.startingParameters) {
            this._load();
        }
    }

    private _load() {
        if (this.props.snippet.inputs.length > 0) {
            this._store = this.props.store || new SnippetFormStore();
            this._store.addListener(this._getStateFromStore);
            this._source = new SnippetFormSource(this.context.pageContext, this._store.getInputType);
            this._loadSnippet(this.props.snippet, this.props.startingParameters);
        } else {
            // if passed a snippet without inputs, bypass the ui and just export the snippet
            this._export({}, TelemetryConstants.ExportSnippet);
        }
    }

    private _loadSnippet(snippet: YamlSnippet, startingParameters?: { [key: string]: string }) {
        try {
            this._store.loadSnippet(snippet, startingParameters);

            if (!this._store.isSnippetSupported) {
                const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService &&
                    telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.LoadedUnsupportedSnippet, {
                        name: this.props.snippet.friendlyName,
                        version: this.props.snippet.version,
                        isTemplate: Boolean(this.props.snippet.content),
                        unsupportedInputs: this._store.unsupporedInputTypes.join(",")
                    });
            }

            if (Boolean(startingParameters)) {
                const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService &&
                    telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.SnippetSelected, {
                        name: snippet.friendlyName,
                        version: snippet.version,
                        codeLens: true
                    });
            }
        } catch (error) {
            this.setState({
                error
            });

            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService &&
                telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.Error, {
                    location: "loadSnippetForm",
                    error
                });
        }
    }

    private _addSnippetToYaml = () => {
        this._export(this._store.getValuesForExport(), TelemetryConstants.ExportSnippet);
    };

    private async _export(values: { [key: string]: string }, telemetryEvent: string) {
        // if the parent wants to know about the input values submitted
        this.props.onInputsCompleted && this.props.onInputsCompleted(values);

        // if the parent wants to know when yaml is generated
        if (this.props.onYamlGenerated) {
            const exportString = await exportYaml(this.context.pageContext, this.props.snippet, values);
            this.props.onYamlGenerated(exportString);
        }

        this.props.onDismiss && this.props.onDismiss();

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService &&
            telemetryService.publishEvent(TelemetryConstants.Area, telemetryEvent, {
                name: this.props.snippet.friendlyName,
                version: this.props.snippet.version,
                isTemplate: Boolean(this.props.snippet.content)
            });
    }

    private _getStateFromStore = () => {
        this.setState({
            snippetInputsByGroup: this._store.inputsByGroup,
            visibleGroups: this._store.visibleGroups,
            isSnippetSupported: this._store.isSnippetSupported
        });
    };

    private _onError = (location: string, error: Error) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService &&
            telemetryService.publishEvent(TelemetryConstants.Area, TelemetryConstants.Error, {
                location,
                error,
                snippetName: this.props.snippet.friendlyName
            });
    };
}

Components.add("yaml-snippet-form", SnippetForm);
